// Phase 1020 — Password self-service under Profile / Account Settings.
// Phase 1021 — Dual-mode: renders "Set Password" for Google-OAuth users
// with no local password_hash, "Change Password" once they have one.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, ArrowLeft } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePassword() {
  const navigate = useNavigate();
  const [hasPassword, setHasPassword] = useState(null); // null = loading
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/password-status`, { withCredentials: true });
        if (!cancelled) setHasPassword(!!data.has_password);
      } catch (e) {
        if (!cancelled) {
          toast.error('Could not determine account state. Please log in again.');
          navigate('/login');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (form.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setBusy(true);
    try {
      if (hasPassword) {
        await axios.post(`${API}/auth/change-password`, form, { withCredentials: true });
        toast.success('Password changed. Please log in again with your new password.');
        setTimeout(() => { navigate('/login'); }, 1500);
      } else {
        await axios.post(
          `${API}/auth/set-password`,
          { new_password: form.new_password, confirm_password: form.confirm_password },
          { withCredentials: true }
        );
        toast.success('Password set. You can now log in with email and password too.');
        setHasPassword(true);
        setForm({ current_password: '', new_password: '', confirm_password: '' });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save password');
    } finally {
      setBusy(false);
    }
  };

  if (hasPassword === null) {
    return (
      <div className="min-h-screen bg-zinc-50 py-10 px-4">
        <div className="max-w-lg mx-auto text-sm text-zinc-500" data-testid="pw-loading">Loading…</div>
      </div>
    );
  }

  const mode = hasPassword ? 'change' : 'set';
  const title = mode === 'set' ? 'Set Password' : 'Change Password';
  const submitLabel = mode === 'set'
    ? (busy ? 'Saving…' : 'Set Password')
    : (busy ? 'Changing…' : 'Change Password');

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4" data-testid="change-pw-back">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="bg-white border border-zinc-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <Lock size={24} className="text-zinc-900" />
            <h1 className="text-xl font-semibold text-zinc-900" data-testid="pw-title">{title}</h1>
          </div>
          {mode === 'set' && (
            <div className="bg-zinc-50 border border-zinc-200 p-3 text-xs text-zinc-600 mb-4" data-testid="set-pw-hint">
              This account was created via Google Sign-In and has no local password yet.
              Set one to also enable email &amp; password login. Your Google login will continue to work.
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            {mode === 'change' && (
              <div>
                <Label>Current Password *</Label>
                <Input type="password" required autoComplete="current-password"
                       value={form.current_password}
                       onChange={e => setForm({ ...form, current_password: e.target.value })}
                       className="rounded-none mt-1" data-testid="change-pw-current" />
              </div>
            )}
            <div>
              <Label>New Password *</Label>
              <Input type="password" required autoComplete="new-password"
                     value={form.new_password}
                     onChange={e => setForm({ ...form, new_password: e.target.value })}
                     className="rounded-none mt-1" data-testid="change-pw-new" />
              <p className="text-[11px] text-zinc-400 mt-1">At least 8 characters, must contain letters and digits.</p>
            </div>
            <div>
              <Label>Confirm New Password *</Label>
              <Input type="password" required autoComplete="new-password"
                     value={form.confirm_password}
                     onChange={e => setForm({ ...form, confirm_password: e.target.value })}
                     className="rounded-none mt-1" data-testid="change-pw-confirm" />
            </div>
            <div className="pt-2">
              <Button type="submit" disabled={busy} className="w-full rounded-none bg-zinc-950 hover:bg-zinc-800" data-testid="change-pw-submit">
                {submitLabel}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
